import type { Lang } from '@/models/lang';
import { getCurrentJstDayjs, toJstCalendarDate } from '@/utils/date';

import type { Dayjs } from 'dayjs';
import type { MaybeRef } from 'vue';

export const getCalendarStartDate = (date: Dayjs) => {
  return date.startOf('month');
};

export const getCalendarEndDate = (date: Dayjs) => {
  return date.endOf('month');
};

export interface CalendarDay {
  day: Dayjs;
  isCurrentMonth: boolean;
}

/**
 * 月の開始日と終了日を受け取って、カレンダー表示用の配列を生成する
 * @param startDate 開始日
 * @param endDate 終了日
 * @returns カレンダー表示用の配列 CalendarDay[][]
 */
export const getCalendarRowList = (startDate: Dayjs, endDate: Dayjs): CalendarDay[][] => {
  const calendarDayList = [];
  for (let day = startDate; day <= endDate; day = day.add(1, 'day')) {
    calendarDayList.push({ day, isCurrentMonth: true });
  }

  // カレンダー表示が揃うように、前の月の日付を追加
  for (let day = startDate.add(-1, 'day'); day.day() !== 6; day = day.add(-1, 'day')) {
    calendarDayList.unshift({ day, isCurrentMonth: false });
  }

  // 7日ごとに配列を分割
  const calendarRowList = [];
  for (let i = 0; i < calendarDayList.length; i += 7) {
    calendarRowList.push(calendarDayList.slice(i, i + 7));
  }

  return calendarRowList;
};

/**
 * カレンダー表示用の Composable
 */
export const useCalendar = (day: MaybeRef<Dayjs> = getCurrentJstDayjs(), lang: Lang) => {
  const dayRef = toRef(day);

  // カレンダーで選択中の月データ（1ヶ月ごと）
  // 暦日演算を UTC モードで行い、実行環境のTZ・DSTによる日付ずれを防ぐ（toJstCalendarDate 参照）
  const selectedDate = ref<Dayjs>(toJstCalendarDate(dayRef.value));

  // カレンダー表示用の日付を格納する
  const calendarRowList = ref<CalendarDay[][]>([]);

  // 現在選択中のカレンダーの年月（例：2023年2月）
  const selectedYearMonth = computed<string>(() => {
    if (lang === 'en') {
      return `${selectedDate.value.locale('en').format('MMMM')} ${selectedDate.value.locale('en').format('YYYY')}`;
    }
    return `${selectedDate.value.format('YYYY')}年${selectedDate.value.format('M')}月`;
  });

  // 現在選択中のカレンダーの最初の日
  const startDate = computed<Dayjs>(() => getCalendarStartDate(selectedDate.value));

  // 現在選択中のカレンダーの最終日
  const endDate = computed<Dayjs>(() => getCalendarEndDate(selectedDate.value));

  // 前の月を押したときのイベントハンドラ
  const onClickPrevButton = () => {
    selectedDate.value = selectedDate.value.add(-1, 'month');
  };

  // 次の月を押したときのイベントハンドラ
  const onClickNextButton = () => {
    selectedDate.value = selectedDate.value.add(1, 'month');
  };

  // 外部から表示月が変更された場合に追従する
  watch(dayRef, (newDay) => {
    selectedDate.value = toJstCalendarDate(newDay);
  });

  // 表示月の変更に応じてカレンダーグリッドを再構築する
  watch(
    selectedDate,
    () => {
      const startDate = getCalendarStartDate(selectedDate.value);
      const endDate = getCalendarEndDate(selectedDate.value);

      calendarRowList.value = getCalendarRowList(startDate, endDate);
    },
    { immediate: true },
  );

  return { selectedDate, calendarRowList, selectedYearMonth, startDate, endDate, onClickPrevButton, onClickNextButton };
};
